import { BaseService } from "./BaseService";
import type { Imovel, ImovelTipo } from "../scripts/db";

export interface SearchFilters {
  type?: ImovelTipo;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
  parkingSpots?: number;
  city?: string;
  neighborhood?: string;
  sort?: "price_asc" | "price_desc" | "newest";
  page?: number;
  pageSize?: number;
}

export interface SearchResult {
  results: Imovel[];
  total: number;
  page: number;
  pageSize: number;
}

interface BackendImovel {
  code: string;
  type: ImovelTipo;
  bedrooms: number;
  suites: number;
  bathrooms: number;
  parkingSpots: number;
  price: number;
  propertyTax: number | null;
  condoFee: number | null;
  builtArea?: number;
  lotArea?: number;
  occupancyPermit: "s" | "n";
  images: string[];
  featuredIndex: number;
  description: string;
  location: {
    city: string;
    neighborhood: string;
    address: string;
  };
  featured: boolean;
  featuredOrder: number | null;
}

interface PaginatedResponse {
  data: BackendImovel[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function mapBackendToImovel(b: BackendImovel): Imovel {
  return {
    code: b.code,
    type: b.type,
    bedrooms: b.bedrooms,
    suites: b.suites,
    bathrooms: b.bathrooms,
    parkingSpots: b.parkingSpots,
    price: b.price,
    propertyTax: b.propertyTax,
    condoFee: b.condoFee,
    builtArea: b.builtArea,
    lotArea: b.lotArea,
    occupancyPermit: b.occupancyPermit,
    images: b.images,
    featuredIndex: b.featuredIndex,
    description: b.description,
    featured: b.featured,
    featuredOrder: b.featuredOrder,
    location: b.location,
  };
}

export class PropertiesService extends BaseService {
  constructor() {
    super("imoveis");
  }

  async getByCode(code: string): Promise<Imovel | null> {
    const result = await this.get<BackendImovel | null>(`${code}`);
    if (!result) return null;
    return mapBackendToImovel(result);
  }

  async getFeatured(): Promise<Imovel[]> {
    const result = await this.get<BackendImovel[]>("destaque");
    if (!result) return [];
    return result.map(mapBackendToImovel);
  }

  async getNeighborhoods(): Promise<string[]> {
    return this.get<string[]>("neighborhoods");
  }

  async getCities(): Promise<string[]> {
    return this.get<string[]>("cities");
  }

  async search(filters: SearchFilters): Promise<SearchResult> {
    const params: Record<string, string> = {};
    if (filters.type) params.type = filters.type;
    if (filters.bedrooms) params.bedroomsMin = String(filters.bedrooms);
    if (filters.bathrooms) params.bathroomsMin = String(filters.bathrooms);
    if (filters.parkingSpots) params.parkingMin = String(filters.parkingSpots);
    if (filters.minPrice) params.priceMin = String(filters.minPrice);
    if (filters.maxPrice) params.priceMax = String(filters.maxPrice);
    if (filters.city) params.city = filters.city;
    if (filters.neighborhood) params.neighborhood = filters.neighborhood;
    if (filters.sort) params.sort = filters.sort;
    if (filters.page) params.page = String(filters.page);
    if (filters.pageSize) params.limit = String(filters.pageSize);
    const paginated = await this.get<PaginatedResponse>("", { params });
    return {
      results: (paginated?.data || []).map(mapBackendToImovel),
      total: paginated?.total || 0,
      page: paginated?.page || 1,
      pageSize: paginated?.limit || filters.pageSize || 12,
    };
  }
}
